import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import { API_BASE_URL } from '../../utils/api';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setMessage(''); // Clear previous messages

    if (!email) {
      setMessage('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message || 'Subscription successful!');
        setEmail(''); // Clear the input field on success
      } else {
        setMessage(data.message || 'Subscription failed. Please try again.');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-white text-gray-700 py-8 md:py-12">
      {/* Top section with CTA and image */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 md:mb-12">
        <div className="relative bg-[#1A202C] rounded-[2rem] overflow-hidden">
          {/* The background image that covers the right side on md and larger screens */}
          <div className="absolute inset-0 z-0 hidden md:block">
            <img 
              src="/me.png"
              alt="Adrian the Developer"
              className="absolute bottom-0 right-0 h-full w-auto object-cover max-w-[50%]"
            />
          </div>
          <div className="relative z-10 p-6 md:p-12 lg:p-20 flex flex-col md:flex-row items-start justify-between">
            <div className="text-white max-w-lg mb-8 md:mb-0">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4">
                Let's Build Something Great Together
              </h2>
              <p className="text-sm sm:text-base md:text-lg mb-6">
                Ready to turn your ideas into a scalable reality?
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/projectspage" className="bg-[#FFD700] text-[#1A202C] py-2 px-4 md:py-3 md:px-6 rounded-full font-semibold hover:bg-yellow-400 transition-colors">
                  View My Work
                </Link>
                <Link to="/contactpage" className="text-white py-2 px-4 md:py-3 md:px-6 rounded-full font-semibold border border-white hover:bg-white hover:text-[#1A202C] transition-colors">
                  Get in Touch &rarr;
                </Link>
              </div>
            </div>
            {/* Image for small screens, stacked below the text */}
            <div className="md:hidden mt-8 w-full">
              <img
                src="/me.png"
                alt="Adrian the Developer"
                className="w-full h-auto object-cover rounded-3xl"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8 md:mb-12">
          {/* All your column divs remain the same */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-gray-900">Useful Links</h4>
            <ul className="space-y-2">
              <li><Link to="/aboutpage" className="text-gray-600 hover:text-gray-900 transition-colors">About Me</Link></li>
              <li><Link to="/servicespage" className="text-gray-600 hover:text-gray-900 transition-colors">Services</Link></li>
              <li><Link to="/projectspage" className="text-gray-600 hover:text-gray-900 transition-colors">Projects</Link></li>
              <li><Link to="/blogpage" className="text-gray-600 hover:text-gray-900 transition-colors">Blog</Link></li>
              <li><Link to="/contactpage" className="text-gray-600 hover:text-gray-900 transition-colors">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-bold mb-4 text-gray-900">Community</h4>
            <ul className="space-y-2">
              <li><Link to="/servicespage" className="text-gray-600 hover:text-gray-900 transition-colors">Join the Community</Link></li>
              <li><Link to="/projectspage" className="text-gray-600 hover:text-gray-900 transition-colors">Events & Workshops</Link></li>
              <li><Link to="/projectspage" className="text-gray-600 hover:text-gray-900 transition-colors">Resources</Link></li>
              <li><Link to="/contactpage" className="text-gray-600 hover:text-gray-900 transition-colors">Help Center</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-bold mb-4 text-gray-900">Tutoring</h4>
            <ul className="space-y-2">
              <li><Link to="/servicepage" className="text-gray-600 hover:text-gray-900 transition-colors">1:1 Mentorship</Link></li>
              <li><Link to="/projectspage" className="text-gray-600 hover:text-gray-900 transition-colors">Online Courses</Link></li>
              <li><Link to="/contactpage" className="text-gray-600 hover:text-gray-900 transition-colors">Testimonials</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-bold mb-4 text-gray-900">Newsletter</h4>
            <p className="text-gray-600 text-sm mb-4">
              Get the latest updates on coding, tutorials, and events.
            </p>
            <form className="flex flex-col sm:flex-row gap-2" onSubmit={handleSubscribe}>
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                className="w-full py-2 px-4 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#1A202C] disabled:opacity-60"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#1A202C] text-white py-2 px-6 rounded-full font-semibold hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {message && (
              <p className={`text-sm mt-2 ${message.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                {message}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-2">
              By subscribing, you agree to my <a href="#" className="underline hover:text-gray-900">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>

      <hr className="my-6 md:my-8 border-t border-gray-200" />

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between">
        {/* Brand and Copyright */}
        <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-8 mb-4 md:mb-0 text-center sm:text-left">
          <div className="flex items-baseline">
            <span className="text-[#1A202C] font-bold text-3xl">
              AD
              <span className="relative inline-block w-2 h-2 bg-[#FFD700] rounded-full animate-pulse -top-2 left-0"></span>
            </span>
          </div>
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Adrian. All rights reserved.
          </p>
        </div>

        {/* Social Icons */}
        <div className="flex items-center space-x-4">
          <a href="https://www.facebook.com/ebesoh.adrian.9" className="text-gray-400 hover:text-[#1A202C] transition-colors" aria-label="Facebook">
            <Icon name="Facebook" size={20} />
          </a>
          <a href="https://www.instagram.com/ebesohadriantaleh?igsh=MTFuMjBtOXcwNGgzbg==" className="text-gray-400 hover:text-[#1A202C] transition-colors" aria-label="Instagram">
            <Icon name="Instagram" size={20} />
          </a>
          <a href="https://www.linkedin.com/in/ebesoh-adrian" className="text-gray-400 hover:text-[#1A202C] transition-colors" aria-label="LinkedIn">
            <Icon name="Linkedin" size={20} />
          </a>
          <a href="https://x.com/adrianebesoh237" className="text-gray-400 hover:text-[#1A202C] transition-colors" aria-label="Twitter">
            <Icon name="Twitter" size={20} />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;